import PriceTrackModal from '@components/ui/hotelPage&view/PriceTrackModal.vue';
import { ServiceType } from '@clients/PricetrackApi';
import type TripModule from '@itinerary/trip/TripModule';
import { type TrainSearchForm } from '@search/trip/train/TrainSearchForm';
import { TrackingType } from '@itinerary/types';
import { type PriceTrackInfo } from '@store/priceTrack';

export const usePriceTrackModal = () => {
    const priceTrackStore = usePriceTrackStore();
    const authStore = useAuthStore();
    const { login } = useAuth();
    const { open } = usePopOver({
        component: PriceTrackModal,
        zIndex: 9999999999,
    });
    const openPriceTrackModal = async (
        attrs: InstanceType<typeof PriceTrackModal>['$props']
    ) => {
        if (!priceTrackStore.priceTrackInfo) {
            return undefined;
        }

        const getTrackingTitle = () => {
            if (attrs.trackingType === TrackingType.Presell) {
                return 'اطلاع از پیش‌فروش';
            } else if (attrs.trackingType === TrackingType.Capacity) {
                return 'رهگیری ظرفیت';
            } else {
                return 'رهگیری قیمت';
            }
        };
        if (authStore.loggedIn) {
            return await open(attrs);
        }
        const result = await login({
            message: `برای بهره مندی از ${getTrackingTitle()} به حساب کاربری خود وارد شوید.`,
            openedBy: 'trackingBtn',
        });
        function timeout(ms: number) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }
        if (result) {
            await timeout(500);
            return await open(attrs);
        }

        return undefined;
    };
    return {
        openPriceTrackModal,
    };
};

export const notifyTrack = async ({
    type,
    isAutomatic,
}: {
    isAutomatic?: boolean;
    type?: TrackingType;
}) => {
    const itineraryStore = useItineraryStore();
    const currentSearchForm = useCurrentSearchForm<TrainSearchForm>();
    const searchFormStore = useSearchFormStore();
    const { reserveType } = storeToRefs(searchFormStore);
    const currentModule = useCurrentItineraryModule<TripModule>();
    const { minPrice } = storeToRefs(itineraryStore);
    const {
        areAllPackagesFull,
        originCurrentCode,
        destinationName,
        destinationCurrentCode,
        originName,
    } = moduleToRefs(currentModule.value);
    const { departureDate, sellType, originCode, destinationCode } =
        moduleToRefs(currentSearchForm.value);
    const priceTrackStore = usePriceTrackStore();
    const { priceTrackList, trainPreSellAlerts, priceTrackInfo } =
        classToRefs(priceTrackStore);
    const { openPriceTrackModal } = usePriceTrackModal();
    const serviceType = computed(() => {
        switch (reserveType.value) {
            case 'flight':
                return ServiceType.NUMBER_0;
            case 'train':
                return ServiceType.NUMBER_1;
            case 'bus':
                return ServiceType.NUMBER_2;
            case 'taxi':
                return 4 as ServiceType;
            default:
                return null;
        }
    });
    const priceTrack = computed(() => {
        if (type === TrackingType.Presell) {
            const prevList = trainPreSellAlerts.value;
            return prevList.includes(
                `${originCode.value}-${destinationCode.value}`
            );
        } else {
            return priceTrackList.value.find(
                p =>
                    !p.serviceId &&
                    !p.serviceNumber &&
                    p.originCode === originCurrentCode?.value &&
                    p.destinationCode === destinationCurrentCode.value &&
                    p.notify &&
                    p.date &&
                    departureDate.value &&
                    dayConfig.gregory(p.date).isSame(departureDate.value, 'day')
            );
        }
    });
    const commonPriceTrackProps: PriceTrackInfo = {
        originCode: originCurrentCode.value,
        destinationCode: destinationCurrentCode.value,
        origin: originName.value ?? '',
        destination: destinationName.value ?? '',
        englishDestination: destinationCode.value,
        englishOrigin: originCode.value,
        date: departureDate.value
            ? `${dayConfig
                  .gregory(departureDate.value)
                  .format('YYYY-MM-DD')}T00:00Z`
            : undefined,
        arrivalTime: '',
        departureDateTime: departureDate.value!,
        logoUrl: '',
        serviceId: null,
        serviceNumber: null,
        dateString:
            departureDate.value &&
            dayConfig
                .jalali(departureDate.value)
                .format(formatter({ format: 'long' })),
        price: areAllPackagesFull.value ? 0 : minPrice.value ?? undefined,
        serviceType: serviceType.value ?? undefined,
        type,
        priceTrackOnPath: true,
        genderCode: sellType?.value?.code,
        classOfService: undefined,
    };
    if (isAutomatic) {
        priceTrackInfo.value = {
            ...commonPriceTrackProps,
            isAutomatic: true,
            isInternational: !currentSearchForm.value.isDomestic,
        };
        try {
            await priceTrackStore.addPriceTrack();
        } catch (e) {
            reportException(e);
        }
        return undefined;
    }
    if (priceTrack.value) {
        if (typeof priceTrack.value === 'object') {
            try {
                await priceTrackStore.setPriceTrackAlert({
                    priceTrackId: priceTrack.value.trackId,
                    priceHash: priceTrack.value.hashId,
                    notify: false,
                });
                priceTrackStore.loadPriceTracks();
            } catch (error) {
                handleException(error);
            }
        } else if (type === TrackingType.Presell) {
            priceTrackStore.removePreSellAlert(
                `${currentSearchForm.value.originCode}-${currentSearchForm.value.destinationCode}`
            );
        }
    } else {
        priceTrackInfo.value = {
            ...commonPriceTrackProps,
            isInternational: !currentSearchForm.value.isDomestic,
        };
        return await openPriceTrackModal({
            price: minPrice.value === Infinity ? 0 : minPrice.value || 0,
            trackingType:
                type !== undefined
                    ? type
                    : areAllPackagesFull.value
                      ? TrackingType.Capacity
                      : TrackingType.Price,
            trackScope: 'path',
        });
    }
};
